import { short } from "diagnostics-lib/io";
import * as io from "io-ts";
import type { ParsingError } from "../../types";
import type { Converter } from "../types";
import { ConverterErrorType } from "../types";
import {
  JSON_FIELDS,
  assignOptionalFields,
  extractLineValues,
  extractLines,
  extractSensorValues,
} from "../utils/extract";
import { isLeft } from "fp-ts/lib/Either";

const FIELD_NAME_MAPPING: Record<string, string> = {
  SOURCE: "sourceId",
  STARTDATE: "acquisitionStartDate",
  ENDDATE: "acquisitionEndDate",
  CAMPAGNE: "group",
  CYCLE: "group",
  GROUP: "group",
  HISTORIQUE_ESSAI: "label",
  TEST_LABEL: "label",
};

export const convertSourceSnapshotsCSVToJSON: Converter = (input: string) => {
  const lines = extractLines<typeof FIELD_NAME_MAPPING>(input, FIELD_NAME_MAPPING);
  const [sourceId] = extractLineValues(lines, JSON_FIELDS.SOURCE_ID);
  const acquisitionStartDates = extractLineValues(lines, JSON_FIELDS.ACQUISITION_START_DATE);
  const acquisitionEndDates = extractLineValues(lines, JSON_FIELDS.ACQUISITION_END_DATE);
  const labels = extractLineValues(lines, JSON_FIELDS.LABEL);
  const groups = extractLineValues(lines, JSON_FIELDS.GROUP);

  const dateErrors = acquisitionStartDates
    .map((date, i) =>
      date.length > 0
        ? null
        : {
            kind: ConverterErrorType.MISSING_SNAPSHOT_DATE,
            explain: "snapshot date is missing",
            location: { file: "", line: 0 },
            data: {
              key: i,
            },
          },
    )
    .filter(err => err !== null) as ParsingError<ConverterErrorType>[];

  const duplicatedDateErrors = acquisitionStartDates
    .map((date, i, arr) =>
      arr.indexOf(date) === i
        ? null
        : {
            kind: ConverterErrorType.DUPLICATED_SNAPSHOT_DATE,
            explain: "duplicated snapshot date in import",
            location: { file: "", line: 0 },
            data: {
              key: i,
            },
          },
    )
    .filter(err => err !== null) as ParsingError<ConverterErrorType>[];

  const length = Math.max(
    acquisitionStartDates.length,
    acquisitionEndDates.length,
    labels.length,
    groups.length,
  );

  const [sensorValuesByDates, sensorValueErrors] = Array.from({ length }, (_, i) => i).reduce<
    [
      ReturnType<typeof extractSensorValues>["sensorValues"][],
      ReturnType<typeof extractSensorValues>["errors"],
    ]
  >(
    (acc, i) => {
      const { sensorValues, errors } = extractSensorValues(
        lines,
        i,
        true,
        acquisitionStartDates[i],
      );
      if (errors.length) acc[1].push(...errors);
      else acc[0].push(sensorValues);
      return acc;
    },
    [[], []],
  );

  const snapshots = Array.from({ length }, (_, i) => i).flatMap(i =>
    acquisitionStartDates[i]?.length
      ? [
          {
            date: acquisitionStartDates[i],
            sensorValues: sensorValuesByDates[i] || [],
            /**
             * Optional Fields
             * @link {back/src/api/snapshot/index.ts}
             */
            ...assignOptionalFields(
              acquisitionStartDates[i],
              acquisitionEndDates[i],
              labels[i],
              groups[i],
            ),
          },
        ]
      : [],
  );

  const invalidLabels = labels
    .filter(label => isLeft(short(io.string).decode(label)))
    .map(label => ({
      kind: ConverterErrorType.INVALID_LABEL_TOO_LONG,
      explain: "invalid label too long",
      location: { file: "", line: 0 },
      data: { value: label },
    }));

  const invalidGroups = groups
    .filter(group => isLeft(short(io.string).decode(group)))
    .map(group => ({
      kind: ConverterErrorType.INVALID_GROUP_TOO_LONG,
      explain: "invalid group too long",
      location: { file: "", line: 0 },
      data: { value: group },
    }));

  const emptyResults =
    !sourceId && !snapshots.length
      ? [
          {
            kind: ConverterErrorType.EMPTY_DATA_EXTRACTION,
            explain: "no data extracted from file",
            location: { file: "", line: 0 },
            data: { value: { snapshots } },
          },
        ]
      : [];

  return {
    result: { sourceId, snapshots },
    errors: [
      ...dateErrors,
      ...duplicatedDateErrors,
      ...sensorValueErrors,
      ...invalidLabels,
      ...invalidGroups,
      ...emptyResults,
      ...(sourceId && isLeft(short(io.string).decode(sourceId))
        ? [
            {
              kind: ConverterErrorType.INVALID_SOURCE_ID,
              explain: "invalid source id",
              location: { file: "", line: 0 },
              data: { value: sourceId },
            },
          ]
        : []),
    ],
  };
};
